'use client';

import { Link, Typography, useMediaQuery } from '@mui/material';
import Image from 'next/image';
import { usePageAction } from '@repo/hooks/usePageAction';
import { MarketplaceBankingSubProductTypeEnum } from '@repo/supergraph/marketplace/enums';
import { MouseEvent, useCallback } from 'react';
import { scrollToViewPromise } from '../../../TableOfContents/scrollToViewPromise';
import { theme } from '../../../Theme';
import { MOBILE_SCROLL_OFFSET, PRODUCT_NAV_ACTION_NAME_MAP } from './constants';
import { CircleChevron } from './icons/circle-chevron';
import {
  getBankingProductAnchorId,
  MARKETPLACE_BANKING_SUBPRODUCT_ICONS,
  MARKETPLACE_BANKING_SUBPRODUCT_NAV_LABELS,
} from './utils';

type Props = {
  subProduct: MarketplaceBankingSubProductTypeEnum;
  activeAnchorId: string;
  setActiveAnchorId: (anchorId: string) => void;
};

export const ProductNavItem = ({ subProduct, activeAnchorId, setActiveAnchorId }: Props) => {
  const iconSrc = MARKETPLACE_BANKING_SUBPRODUCT_ICONS[subProduct];
  const anchorId = getBankingProductAnchorId(subProduct);
  const isActive = activeAnchorId === anchorId;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const trackProductNavItemInteraction = usePageAction({
    actionName: `anchor_interacted_${PRODUCT_NAV_ACTION_NAME_MAP[subProduct]}`,
    actionSection: 'banking',
  });

  const handleClick = useCallback(
    async (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      const scrollOffset = isMobile ? MOBILE_SCROLL_OFFSET : 0;
      await scrollToViewPromise(anchorId, 'smooth', scrollOffset, true);
      setActiveAnchorId(anchorId);
      trackProductNavItemInteraction();
    },
    [anchorId, isMobile, setActiveAnchorId, trackProductNavItemInteraction],
  );

  const label = MARKETPLACE_BANKING_SUBPRODUCT_NAV_LABELS[subProduct];

  return (
    <Link
      component="a"
      href={`#${anchorId}`}
      onClick={handleClick}
      underline="none"
      aria-label={`Navigate to ${label} section`}
      aria-current={isActive ? 'page' : undefined}
      sx={{
        display: 'flex',
        gap: 2,
        padding: 2,
        alignItems: 'center',
        backgroundColor: 'neutral.lightest',
        borderRadius: 2,
        color: 'neutral.light',
        '&:hover': {
          color: 'primary.main',
          backgroundColor: 'neutral.lighter',
        },
        '&:focus-visible': {
          color: 'primary.main',
          backgroundColor: 'neutral.lighter',
        },
        '@media (hover: none) and (pointer: coarse)': {
          '&:focus': {
            color: 'neutral.light',
            backgroundColor: 'neutral.lightest',
          },
        },
        '&:active': {
          color: 'primary.main',
          backgroundColor: 'neutral.lighter',
        },
        textWrap: 'nowrap',
        cursor: 'pointer',
        textDecoration: 'none',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <Image src={iconSrc} alt="" width={25} height={24} aria-hidden />
      <Typography
        variant="textBold"
        sx={{
          flex: 1,
          textAlign: 'left',
          color: '#3D3D3B',
        }}
      >
        {MARKETPLACE_BANKING_SUBPRODUCT_NAV_LABELS[subProduct]}
      </Typography>
      <CircleChevron aria-hidden="true" />
    </Link>
  );
};
